// src/features/Planning/components/Step11FloorSpaceRequirements.jsx
import {
  Box,
  Text,
  Radio,
  RadioGroup,
  Button,
  Flex,
  Input,
  Checkbox,
  CheckboxGroup,
  SimpleGrid,
  Image,
  Textarea,
  VStack,
  HStack,
  Circle,
  IconButton,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import closeKitchen from "../../../../assets/Planning/FloorRequirement/closekitchen.jpg";
import OpenKitchen from "../../../../assets/Planning/FloorRequirement/openkitchen.jpg";

const additionalSpaces = [
  "Living Room",
  "Dining Area",
  "Drawing Room",
  "Pooja Room",
  "Study Room",
  "Gym Room",
  "Utility/Wash Area",
  "Dressing Area",
  "Recreation Area",
  "Garden",
  "Foyer",
  "Balcony",
  "Lift",
  "Office",
  "Theatre Room",
  "Shop",
];

// +/- Number Input component
function NumberStepInput({ value, onChange, min = 0, max = 20 }) {
  const numVal = parseInt(value) || 0;

  const decrement = () => {
    if (numVal > min) onChange(String(numVal - 1));
  };
  const increment = () => {
    if (numVal < max) onChange(String(numVal + 1));
  };

  return (
    <HStack spacing={1}>
      <IconButton
        icon={<Text fontWeight="700" fontSize="16px">−</Text>}
        onClick={decrement}
        size="sm"
        h="40px"
        w="36px"
        minW="36px"
        borderRadius="8px"
        bg="gray.100"
        color="gray.700"
        _hover={{ bg: "red.100", color: "red.600" }}
        aria-label="Decrease"
      />
      <Input
        type="number"
        min={min}
        max={max}
        h="40px"
        value={numVal}
        onChange={(e) => onChange(e.target.value)}
        w="64px"
        textAlign="center"
        borderRadius="8px"
        fontSize="15px"
        fontWeight="600"
        borderColor="gray.300"
        _hover={{ borderColor: "cyan.400" }}
        _focus={{
          borderColor: "cyan.500",
          boxShadow: "0 0 0 1px var(--chakra-colors-cyan-500)",
        }}
      />
      <IconButton
        icon={<Text fontWeight="700" fontSize="16px">+</Text>}
        onClick={increment}
        size="sm"
        h="40px"
        w="36px"
        minW="36px"
        borderRadius="8px"
        bg="gray.100"
        color="gray.700"
        _hover={{ bg: "green.100", color: "green.600" }}
        aria-label="Increase"
      />
    </HStack>
  );
}

export default function Step11FloorSpaceRequirements({
  formData,
  setFormData,
  onNext,
  onBack,
  isLastStep,
  onSubmit,
  viewMode,
}) {
  const floorsCount = (() => {
    if (!formData?.numberOfFloors) return 1;
    if (typeof formData.numberOfFloors === "string") {
      if (formData.numberOfFloors.includes("+")) {
        const match = formData.numberOfFloors.match(/\d+/);
        const additional = match ? parseInt(match[0]) : 0;
        return 1 + additional;
      } else if (formData.numberOfFloors === "ground") {
        return 1;
      } else {
        const match = formData.numberOfFloors.match(/\d+/);
        return match ? parseInt(match[0]) : 1;
      }
    }
    return 1;
  })();

  const [activeFloor, setActiveFloor] = useState(0);
  const [floorsData, setFloorsData] = useState(() => {
    if (formData?.floorsData && formData.floorsData.length > 0) {
      return formData.floorsData;
    }
    return Array(floorsCount)
      .fill()
      .map(() => ({
        masterBedroomsWithToilet: "",
        bedroomsWithoutToilet: "",
        kitchen: "",
        kitchenType: "",
        additionalSpaces: [],
        otherSpaces: "",
      }));
  });

  useEffect(() => {
    if (formData?.floorsData && formData.floorsData.length > 0) {
      setFloorsData(formData.floorsData);
    }
  }, [formData?.floorsData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (floorsData.length !== floorsCount) {
      const newFloorsData = Array(floorsCount)
        .fill()
        .map((_, idx) =>
          floorsData[idx] ?? {
            masterBedroomsWithToilet: "",
            bedroomsWithoutToilet: "",
            kitchen: "",
            kitchenType: "",
            additionalSpaces: [],
            otherSpaces: "",
          }
        );
      setFloorsData(newFloorsData);
    }
  }, [floorsCount]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeFloor >= floorsCount) {
      setActiveFloor(0);
    }
  }, [activeFloor, floorsCount]);

  const current = floorsData[activeFloor] ?? {
    masterBedroomsWithToilet: "",
    bedroomsWithoutToilet: "",
    kitchen: "",
    kitchenType: "",
    additionalSpaces: [],
    otherSpaces: "",
  };

  const updateFloorField = (field, value) => {
    const updated = [...floorsData];
    updated[activeFloor] = { ...updated[activeFloor], [field]: value };
    setFloorsData(updated);
  };

  const handleCheckbox = (spaces) => {
    updateFloorField("additionalSpaces", spaces);
  };

  const handleNext = () => {
    if (activeFloor < floorsCount - 1) {
      setActiveFloor((f) => f + 1);
    } else {
      const mergedData = { ...formData, floorsData };
      setFormData(mergedData);
      if (isLastStep) onSubmit(mergedData);
      else onNext(mergedData);
    }
  };

  const handlePrev = () => {
    if (activeFloor > 0) {
      setActiveFloor((f) => f - 1);
    } else {
      onBack();
    }
  };

  function getOrdinalSuffix(num) {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return "st";
    if (j === 2 && k !== 12) return "nd";
    if (j === 3 && k !== 13) return "rd";
    return "th";
  }

  function floorLabel(idx) {
    if (idx === 0) return "Ground Floor";
    if (idx === 1) return "First Floor";
    if (idx === 2) return "Second Floor";
    if (idx === 3) return "Third Floor";
    const floorNum = idx;
    return `${floorNum}${getOrdinalSuffix(floorNum)} Floor`;
  }

  // Kitchen image logic — open/close ke hisaab se, no type selected to no image
  const kitchenImage =
    current.kitchenType === "open"
      ? OpenKitchen
      : current.kitchenType === "close"
      ? closeKitchen
      : null;

  return (
    <Box
      h="600px"
      w="100%"
      maxW="400px"
      mx="auto"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      px={4}
      py={6}
    >
      {/* Floor Progress Indicator */}
      <Box mb={4}>
        <HStack spacing={2} justify="center" mb={2}>
          {Array.from({ length: floorsCount }).map((_, idx) => (
            <HStack key={idx} spacing={1}>
              <Circle
                size="32px"
                bg={idx === activeFloor ? "cyan.500" : idx < activeFloor ? "cyan.200" : "gray.200"}
                color={idx === activeFloor ? "white" : idx < activeFloor ? "cyan.700" : "gray.500"}
                fontWeight="600"
                fontSize="13px"
                border="2px solid"
                borderColor={idx === activeFloor ? "cyan.600" : "transparent"}
                transition="all 0.3s ease"
              >
                {idx + 1}
              </Circle>
              {idx < floorsCount - 1 && (
                <Box
                  w="20px"
                  h="2px"
                  bg={idx < activeFloor ? "cyan.300" : "gray.300"}
                  transition="all 0.3s ease"
                />
              )}
            </HStack>
          ))}
        </HStack>
        <Text textAlign="center" fontSize="14px" fontWeight="600" color="cyan.600">
          {floorLabel(activeFloor)} Space Requirements
        </Text>
        <Text textAlign="center" fontSize="12px" color="gray.500" mt={1}>
          Step {activeFloor + 1} of {floorsCount}
        </Text>
      </Box>

      {/* Scrollable Content Area */}
      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        pr={2}
        css={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: "10px" },
          "&::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "10px" },
          "&::-webkit-scrollbar-thumb:hover": { background: "#a0aec0" },
          "& *": { pointerEvents: viewMode ? "none" : "auto" },
        }}
      >
        <VStack spacing={4} align="stretch">

          {/* Master Bedroom with Toilet */}
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="10px"
            p={3}
          >
            <Text flex="1" fontWeight="500" fontSize="14px" color="gray.700" mb={3}>
              Master Bedroom with Toilet
            </Text>
            <NumberStepInput
              value={current.masterBedroomsWithToilet}
              onChange={(v) => updateFloorField("masterBedroomsWithToilet", v)}
            />
          </Box>

          {/* Bedrooms Without Toilet */}
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="10px"
            p={3}
          >
            <Text flex="1" fontWeight="500" fontSize="14px" color="gray.700" mb={3}>
              Number of Bedrooms Without Toilet
            </Text>
            <NumberStepInput
              value={current.bedroomsWithoutToilet}
              onChange={(v) => updateFloorField("bedroomsWithoutToilet", v)}
            />
          </Box>

          {/* Kitchen Yes/No */}
          <Box>
            <Text mb={2} fontWeight="500" fontSize="14px" color="gray.700">
              Kitchen
            </Text>
            <RadioGroup
              value={current.kitchen}
              onChange={(v) => updateFloorField("kitchen", v)}
            >
              <Flex gap={6}>
                <Radio value="yes" colorScheme="cyan" size="md">
                  <Text fontSize="14px" fontWeight="500" color="gray.700">Yes</Text>
                </Radio>
                <Radio value="no" colorScheme="cyan" size="md">
                  <Text fontSize="14px" fontWeight="500" color="gray.700">No</Text>
                </Radio>
              </Flex>
            </RadioGroup>
          </Box>

          {/* Kitchen Type */}
          {current.kitchen === "yes" && (
            <Box>
              <Text mb={2} fontWeight="500" fontSize="14px" color="gray.700">
                Kitchen type
              </Text>
              <RadioGroup
                value={current.kitchenType}
                onChange={(v) => updateFloorField("kitchenType", v)}
              >
                <Flex gap={6}>
                  <Radio value="open" colorScheme="cyan" size="md">
                    <Text fontSize="14px" fontWeight="500" color="gray.700">Open</Text>
                  </Radio>
                  <Radio value="close" colorScheme="cyan" size="md">
                    <Text fontSize="14px" fontWeight="500" color="gray.700">Close</Text>
                  </Radio>
                </Flex>
              </RadioGroup>

              {/* Kitchen image — open/close ke hisaab se switch hogi */}
              {kitchenImage && (
                <Image
                  src={kitchenImage}
                  alt={current.kitchenType === "open" ? "Open Kitchen" : "Close Kitchen"}
                  w="100%"
                  h="120px"
                  objectFit="cover"
                  borderRadius="12px"
                  mt={3}
                  border="1px solid"
                  borderColor="gray.200"
                />
              )}
            </Box>
          )}

          {/* Additional Required Spaces */}
          <Box>
            <Text mb={3} fontWeight="500" fontSize="14px" color="gray.700">
              Additional Required Spaces
            </Text>
            <CheckboxGroup value={current.additionalSpaces} onChange={handleCheckbox}>
              <SimpleGrid columns={2} spacing={3}>
                {additionalSpaces.map((name) => (
                  <Checkbox size="md" colorScheme="cyan" value={name} key={name}>
                    <Text fontSize="14px" fontWeight="500" color="gray.700">
                      {name}
                    </Text>
                  </Checkbox>
                ))}
              </SimpleGrid>
            </CheckboxGroup>
          </Box>

          {/* Other (Please Specify) */}
          <Box>
            <Text mb={2} fontWeight="500" fontSize="14px" color="gray.700">
              Other (Please Specify)
            </Text>
            <Textarea
              placeholder="Enter additional spaces..."
              value={current.otherSpaces}
              onChange={(e) => updateFloorField("otherSpaces", e.target.value)}
              borderColor="gray.300"
              borderRadius="8px"
              minH="80px"
              fontSize="14px"
              _hover={{ borderColor: "cyan.400" }}
              _focus={{
                borderColor: "cyan.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-cyan-500)",
              }}
            />
          </Box>
        </VStack>
      </Box>

      {/* Fixed Navigation Buttons */}
      <Flex justify="space-between" gap={3} mt={4} flexShrink={0}>
        <Button
          onClick={handlePrev}
          variant="solid"
          bg="white"
          color="cyan.600"
          border="1px solid"
          borderColor="cyan.500"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          _hover={{ bg: "cyan.50", borderColor: "cyan.600" }}
        >
          ← Previous
        </Button>
        <Button
          onClick={handleNext}
          variant="solid"
          bg="cyan.500"
          color="white"
          border="none"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          ml="auto"
          _hover={{ bg: "cyan.600" }}
        >
          {activeFloor < floorsCount - 1 ? "Next Floor →" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}